import { looksLikeEmail, PASSWORD_PROBLEM_TEXT, passwordProblem, SIGNUP_LINK_TTL_HOURS } from '@calling/shared';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, NetworkError } from '../lib/api';
import { joinList } from '../lib/format';
import { APP_NAME, isNative, platform } from '../lib/platform';
import { useServerInfo } from '../lib/serverInfo';
import { PasswordField } from './PasswordField';
import { AgreeLine } from './Shell';
import { Banner } from './ui';

type Mode = 'signin' | 'signup' | 'check' | 'forgot' | 'forgot-sent';

/** What went wrong, for a banner: the server's own words where it has them. */
export function requestProblem(err: unknown, fallback: string): { title: string; body: string } {
  if (err instanceof NetworkError) return { title: 'Can’t reach the server', body: 'Check your connection and try again.' };
  if (err instanceof ApiError && err.status === 429) return { title: 'Please wait a little', body: err.message };
  if (err instanceof ApiError && err.status === 503) return { title: 'Busy right now', body: err.message };
  if (err instanceof ApiError && err.status < 500) return { title: fallback, body: err.message };
  return { title: fallback, body: 'Something went wrong on our side. Try again in a moment.' };
}

/**
 * On a server that sends no email, says where the email went instead: the
 * development mailbox, or the server's log. Nothing on a server that sends it.
 */
export function EmailNotSentNote() {
  const { info } = useServerInfo();
  if (!info || info.emailEnabled) return null;
  if (info.devMailbox) {
    return (
      <Banner tone="info" title="This server sends no email" testId="dev-mailbox-note">
        It is a development server without a mail server. Open the{' '}
        <Link to="/dev/mailbox" target="_blank">
          development mailbox
        </Link>{' '}
        to see what it would have sent.
      </Banner>
    );
  }
  return (
    <Banner tone="info" title="This server sends no email" testId="no-email-note">
      No mail server is set up, so the email was written to the server’s log instead.
    </Banner>
  );
}

const post = <T,>(path: string, body: unknown) => api.request<T>(path, { method: 'POST', body, auth: 'none' });

/**
 * Signing in, and signing up, with an email address and a password, inside
 * the page or the app: no browser is opened. A sign-up is finished from the
 * link we email, on whatever device the person opens it; the "Check your
 * email" screen then signs this device in with the password just typed,
 * which it keeps only while it is on screen.
 *
 * `onSignedIn` replaces going to `returnTo` afterwards (a dialog closes
 * instead); `onBack` offers the other ways to sign in again.
 */
export function EmailSignIn({
  returnTo,
  onSignedIn,
  onBack,
  initialMode = 'signin',
  autoFocus = false,
}: {
  returnTo: string;
  onSignedIn?: () => void;
  onBack?: () => void;
  initialMode?: 'signin' | 'signup';
  /** Put the cursor in the first field at once: the person has just chosen email. Otherwise only when they move between its screens. */
  autoFocus?: boolean;
}) {
  const navigate = useNavigate();
  const { info } = useServerInfo();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string }>({});
  const [banner, setBanner] = useState<{ title: string; body: ReactNode; testId?: string; action?: ReactNode; tone?: 'info' } | null>(null);
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  const shown = useRef(false);
  const providers = info?.providers ?? [];

  // Each screen starts with the cursor in its first field; a page that opens on the form is not scrolled to it.
  useEffect(() => {
    if (shown.current || autoFocus) first.current?.focus();
    shown.current = true;
    // Only when the screen changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const go = (next: Mode) => {
    setMode(next);
    setErrors({});
    setBanner(null);
    if (next !== 'check') setPassword('');
  };
  const done = () => {
    setPassword('');
    if (onSignedIn) onSignedIn();
    else navigate(returnTo, { replace: true });
  };

  const checkEmail = (): string | null => {
    const address = email.trim();
    if (looksLikeEmail(address)) return address;
    setErrors((e) => ({ ...e, email: 'Enter an email address like name@example.com.' }));
    return null;
  };

  const resend = async (to: string) => {
    try {
      await post('/api/auth/password/resend', { email: to, platform });
      setBanner({ tone: 'info', title: 'We sent the link again', body: `Check ${to}, and your spam folder.`, testId: 'link-resent' });
    } catch (err) {
      setBanner({ ...requestProblem(err, 'The link could not be sent'), testId: 'email-error' });
    }
  };

  const signIn = async (e: FormEvent) => {
    e.preventDefault();
    setBanner(null);
    const address = checkEmail();
    if (!password) setErrors((x) => ({ ...x, password: 'Enter your password.' }));
    if (!address || !password) return;
    setBusy(true);
    try {
      await api.signInWithPassword(address, password);
      done();
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === 'credentials_invalid') {
        setBanner({ title: 'Email or password is incorrect', body: 'Check both and try again. Forgot your password? You can choose a new one.', testId: 'email-error' });
      } else if (err instanceof ApiError && err.code === 'email_not_verified') {
        setBanner({
          title: 'Confirm your email address first',
          // The same words whether or not the address has an account (see the server): an owner who signed up again by mistake reads the last sentence.
          body: `We sent a link to ${address} when you signed up. Open it to finish creating your account. If you already have an account with this address, the email says how to sign in instead.`,
          testId: 'email-error',
          action: (
            <button type="button" className="btn" onClick={() => void resend(address)}>
              Send the link again
            </button>
          ),
        });
      } else {
        setBanner({ ...requestProblem(err, 'Sign-in did not work'), testId: 'email-error' });
      }
    }
  };

  const signUp = async (e: FormEvent) => {
    e.preventDefault();
    setBanner(null);
    const problems: typeof errors = {};
    if (!name.trim()) problems.name = 'Enter your name (up to 40 characters).';
    const address = email.trim();
    if (!looksLikeEmail(address)) problems.email = 'Enter an email address like name@example.com.';
    const rule = passwordProblem(password, { email: address, name, appName: APP_NAME });
    if (rule) problems.password = PASSWORD_PROBLEM_TEXT[rule];
    setErrors(problems);
    if (Object.keys(problems).length > 0) return;
    setBusy(true);
    try {
      await post('/api/auth/password/signup', { name, email: address, password, platform, returnTo });
      setEmail(address);
      go('check');
    } catch (err) {
      // What only the server checks in full (its longer list of common passwords) belongs under the field.
      if (err instanceof ApiError && err.code.startsWith('password_')) setErrors({ password: err.message });
      else if (err instanceof ApiError && err.code === 'email_invalid') setErrors({ email: err.message });
      else if (err instanceof ApiError && err.code === 'name_invalid') setErrors({ name: err.message });
      else setBanner({ ...requestProblem(err, 'Your account could not be created'), testId: 'email-error' });
    } finally {
      setBusy(false);
    }
  };

  const forgot = async (e: FormEvent) => {
    e.preventDefault();
    setBanner(null);
    const address = checkEmail();
    if (!address) return;
    setBusy(true);
    try {
      await post('/api/auth/password/forgot', { email: address, platform });
      setEmail(address);
      go('forgot-sent');
    } catch (err) {
      setBanner({ ...requestProblem(err, 'The link could not be sent'), testId: 'email-error' });
    } finally {
      setBusy(false);
    }
  };

  // A page shown inside another website's frame never asks for a password: that page could be made to look like ours.
  if (typeof window !== 'undefined' && window.self !== window.top) {
    return (
      <Banner tone="info" title={`Open ${APP_NAME} in its own tab`} testId="email-framed">
        To sign in with your email address,{' '}
        <a href={window.location.href} target="_blank" rel="noopener">
          open {APP_NAME} in a new tab
        </a>
        .
      </Banner>
    );
  }

  const shownBanner = banner ? (
    <Banner tone={banner.tone ?? 'danger'} title={banner.title} testId={banner.testId} action={banner.action}>
      {banner.body}
    </Banner>
  ) : null;
  const back = onBack ? (
    <button type="button" className="link-btn email-panel__back" onClick={onBack}>
      ← Other ways to sign in
    </button>
  ) : null;
  const emailField = (autoComplete: 'username' | 'email') => (
    <div className="field">
      <label htmlFor="email-address">Email address</label>
      <input
        id="email-address"
        ref={mode === 'signup' ? undefined : first}
        className="input"
        type="email"
        inputMode="email"
        name="email"
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        maxLength={254}
        value={email}
        aria-invalid={errors.email ? true : undefined}
        aria-describedby={errors.email ? 'email-address-error' : undefined}
        onChange={(e) => {
          setEmail(e.target.value);
          setErrors((x) => ({ ...x, email: undefined }));
        }}
      />
      {errors.email ? (
        <span className="error" id="email-address-error" role="alert">
          {errors.email}
        </span>
      ) : null}
    </div>
  );

  if (mode === 'check') {
    return <CheckEmail email={email} password={password} onSignedIn={done} onOtherAddress={() => go('signup')} onBack={back} />;
  }

  if (mode === 'forgot-sent') {
    return (
      <div className="email-panel stack" data-testid="forgot-sent">
        <h2 className="email-panel__title">Check your email</h2>
        <p className="email-panel__text">
          If {email} has a {APP_NAME} account, we’ve sent it an email. The link to choose a new password works for 30 minutes.
          {providers.length > 0 ? ` If you sign in with ${joinList(providers.map((p) => p.label))} instead, the email tells you how to get back in.` : ''}
        </p>
        <EmailNotSentNote />
        <p className="muted email-panel__small">No email? Check your spam folder.</p>
        <button type="button" className="btn btn--block" onClick={() => go('signin')}>
          Back to sign in
        </button>
      </div>
    );
  }

  if (mode === 'forgot') {
    return (
      <form className="email-panel stack" method="post" noValidate onSubmit={forgot} data-testid="email-forgot">
        {back}
        <h2 className="email-panel__title">Forgot your password?</h2>
        <p className="email-panel__text muted">Enter your email address. If your account has a password, we’ll email you a link to choose a new one.</p>
        {emailField('username')}
        {shownBanner}
        <button className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Sending…' : 'Send link'}
        </button>
        <button type="button" className="link-btn email-panel__small email-panel__link" onClick={() => go('signin')}>
          Back to sign in
        </button>
      </form>
    );
  }

  if (mode === 'signup') {
    return (
      <form className="email-panel stack" method="post" noValidate onSubmit={signUp} data-testid="email-signup">
        {back}
        <h2 className="email-panel__title">Create your account</h2>
        <div className="field">
          <label htmlFor="signup-name">Your name</label>
          <input
            id="signup-name"
            ref={first}
            className="input"
            name="name"
            autoComplete="name"
            maxLength={40}
            value={name}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby="signup-name-note"
            onChange={(e) => {
              setName(e.target.value);
              setErrors((x) => ({ ...x, name: undefined }));
            }}
          />
          {errors.name ? (
            <span className="error" id="signup-name-note" role="alert">
              {errors.name}
            </span>
          ) : (
            <span className="hint" id="signup-name-note">
              Shown to others in your meetings.
            </span>
          )}
        </div>
        {emailField('username')}
        <PasswordField
          label="Choose a password"
          autoComplete="new-password"
          value={password}
          strength={{ email, name, appName: APP_NAME }}
          error={errors.password}
          onChange={(value) => {
            setPassword(value);
            setErrors((x) => ({ ...x, password: undefined }));
          }}
          onBlur={() => {
            const rule = password ? passwordProblem(password, { email, name, appName: APP_NAME }) : null;
            if (rule) setErrors((x) => ({ ...x, password: PASSWORD_PROBLEM_TEXT[rule] }));
          }}
        />
        {shownBanner}
        <button className="btn btn--primary btn--block" disabled={busy}>
          {busy ? 'Creating your account…' : 'Create account'}
        </button>
        <p className="muted email-panel__small">We’ll email you a link to confirm your address. Your account is ready once you open it.</p>
        <AgreeLine action="continuing" />
        <p className="email-panel__small">
          Already have an account?{' '}
          <button type="button" className="link-btn" onClick={() => go('signin')}>
            Sign in
          </button>
        </p>
      </form>
    );
  }

  return (
    <form className="email-panel stack" method="post" noValidate onSubmit={signIn} data-testid="email-signin">
      {back}
      <h2 className="email-panel__title">Sign in with your email</h2>
      {emailField('username')}
      <PasswordField
        label="Password"
        autoComplete="current-password"
        value={password}
        error={errors.password}
        onChange={(value) => {
          setPassword(value);
          setErrors((x) => ({ ...x, password: undefined }));
        }}
      />
      {shownBanner}
      <button className="btn btn--primary btn--block" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      <button type="button" className="link-btn email-panel__small email-panel__link" onClick={() => go('forgot')}>
        Forgot your password?
      </button>
      <AgreeLine action="continuing" />
      <p className="email-panel__small">
        New to {APP_NAME}?{' '}
        <button type="button" className="link-btn" onClick={() => go('signup')}>
          Create an account
        </button>
      </p>
    </form>
  );
}

/** Seconds before the link can be sent again: the server sends one email a minute to an address. */
const RESEND_WAIT_SEC = 60;
/**
 * How many times the screen tries by itself, on coming back to it. Each try
 * that the server answers "incorrect" counts towards locking the address
 * (5 in 15 minutes), so it stops well before that.
 */
const QUIET_TRIES = 3;

/**
 * Said when the password typed at sign-up opens nothing any more: the sign-up
 * was replaced by a newer one for the address, or it ran out. (A sign-up for
 * an address that already has an account is kept as any other, and answers
 * "not confirmed yet": its owner was emailed instead of being sent a link.
 * It ends here too once that account's password changes.)
 */
const SIGNUP_ENDED = {
  tone: 'info' as const,
  title: 'This sign-up no longer works',
  body: 'It was replaced by a newer sign-up for this address, or it ran out. Sign up again to get a new link.',
  testId: 'signup-ended',
};

/**
 * After signing up: the link is on its way. Once it has been opened and the
 * password typed there, this device signs in with the same password, by
 * itself when the person comes back to it, or with "I've confirmed it".
 */
export function CheckEmail({ email, password, onSignedIn, onOtherAddress, onBack }: { email: string; password: string; onSignedIn: () => void; onOtherAddress: () => void; onBack: ReactNode }) {
  const [wait, setWait] = useState(RESEND_WAIT_SEC);
  const [banner, setBanner] = useState<{ tone: 'info' | 'danger'; title: string; body: string; testId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const trying = useRef(false);
  const lastTry = useRef(0);
  const quietTries = useRef(0);
  // Set once the server says the password is incorrect: trying again by itself would only lock the address.
  const stopped = useRef(false);

  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setTimeout(() => setWait(wait - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [wait]);

  /** Signs in with the password typed at sign-up. `quiet`: on coming back to the app or tab, when not confirming yet is no news. */
  const attempt = async (quiet: boolean) => {
    if (trying.current) return;
    trying.current = true;
    lastTry.current = Date.now();
    if (!quiet) setBusy(true);
    try {
      // Confirming in this same browser signed it in already.
      if (!isNative && (await api.refresh())) return onSignedIn();
      if (quiet && (stopped.current || quietTries.current >= QUIET_TRIES)) return;
      if (quiet) quietTries.current += 1;
      await api.signInWithPassword(email, password);
      onSignedIn();
    } catch (err) {
      // Only "not confirmed yet" means keep waiting. "Incorrect" means this
      // sign-up is gone, and trying again would only count towards a lock.
      if (err instanceof ApiError && err.code === 'credentials_invalid') {
        stopped.current = true;
        setBanner(SIGNUP_ENDED);
        return;
      }
      if (quiet) return;
      if (err instanceof ApiError && err.code === 'email_not_verified') {
        setBanner({
          tone: 'info',
          title: 'Not confirmed yet',
          body: 'Open the link in the email first, and enter your password there. If you already have an account with this address, the email says how to sign in instead.',
          testId: 'not-confirmed',
        });
      } else {
        setBanner({ tone: 'danger', ...requestProblem(err, 'Sign-in did not work'), testId: 'email-error' });
      }
    } finally {
      trying.current = false;
      setBusy(false);
    }
  };

  // Coming back to the app, or to this tab, after confirming in the email.
  useEffect(() => {
    const quietly = () => {
      if (Date.now() - lastTry.current > 5000) void attempt(true);
    };
    if (isNative) {
      let remove: (() => void) | undefined;
      let gone = false;
      void import('@capacitor/app').then(async ({ App }) => {
        const handle = await App.addListener('resume', quietly);
        if (gone) void handle.remove();
        else remove = () => void handle.remove();
      });
      return () => {
        gone = true;
        remove?.();
      };
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') quietly();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // Set up once for this screen; `attempt` reads only what does not change while it is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resend = async () => {
    setWait(RESEND_WAIT_SEC);
    try {
      await post('/api/auth/password/resend', { email, platform });
      setBanner({ tone: 'info', title: 'We sent the link again', body: `Check ${email}, and your spam folder.`, testId: 'link-resent' });
    } catch (err) {
      setBanner({ tone: 'danger', ...requestProblem(err, 'The link could not be sent'), testId: 'email-error' });
    }
  };

  return (
    <div className="email-panel stack" data-testid="check-email">
      {onBack}
      <h2 className="email-panel__title">Check your email</h2>
      <p className="email-panel__text">
        We sent a link to <b>{email}</b>. Open it on any device and enter your password there to finish creating your account. It works for {SIGNUP_LINK_TTL_HOURS} hours.
        {isNative ? ' Then come back here.' : ''}
      </p>
      <EmailNotSentNote />
      {banner ? (
        <Banner tone={banner.tone} title={banner.title} testId={banner.testId}>
          {banner.body}
        </Banner>
      ) : null}
      <button type="button" className="btn btn--primary btn--block" disabled={busy} onClick={() => void attempt(false)}>
        {busy ? 'Checking…' : 'I’ve confirmed it'}
      </button>
      <button type="button" className="btn btn--block" disabled={wait > 0} onClick={() => void resend()}>
        {wait > 0 ? `Send the link again in ${wait} s` : 'Send the link again'}
      </button>
      <p className="muted email-panel__small">
        No email? Check your spam folder.{' '}
        <button type="button" className="link-btn" onClick={onOtherAddress}>
          Use a different address
        </button>
      </p>
    </div>
  );
}
